import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from './Navigation';
import apiService from '../services/api';
import { FUEL_TYPES, BODY_TYPES, ALL_BRANDS, POPULAR_BRANDS } from '../constants/carFilters';
import Spinner from './Spinner';
import EnableNotifications from './EnableNotifications';

// Origin for locally-hosted car images (car.imageUrl is a path like /api/cars/{id}/image).
const IMAGE_ORIGIN = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080';

function CarsTable() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [stats, setStats] = useState(null);
  const [animatedCarIds, setAnimatedCarIds] = useState(new Set());
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  
  // Filter states
  const [filters, setFilters] = useState({
    selectedBrands: [...ALL_BRANDS], // All brands selected initially
    model: '',
    priceFrom: '',
    priceTo: '',
    letnikFrom: '',
    letnikTo: '',
    mileageFrom: '',
    mileageTo: '',
    gorivo: '',
    karoserija: '',
    viewLocation: '',
    sellerTypes: {
      trader: false, // Trgovec onemogocen — filtriramo samo fizicne osebe
      individual: true
    },
    ageTypes: {
      novo: true,
      rabljeno: true
    }
  });

  const [filterSaved, setFilterSaved] = useState(false);

  // Map a saved filter (from the server) back into the local filter state.
  const applyServerFilter = (dto) => {
    if (!dto) return;
    setFilters((prev) => ({
      ...prev,
      selectedBrands: (dto.brands && dto.brands.length) ? dto.brands : [...ALL_BRANDS],
      model: dto.model || '',
      priceFrom: dto.minPrice != null ? String(dto.minPrice) : '',
      priceTo: dto.maxPrice != null ? String(dto.maxPrice) : '',
      letnikFrom: dto.minYear != null ? String(dto.minYear) : '',
      letnikTo: dto.maxYear != null ? String(dto.maxYear) : '',
      mileageFrom: dto.minMileage != null ? String(dto.minMileage) : '',
      mileageTo: dto.maxMileage != null ? String(dto.maxMileage) : '',
      gorivo: (dto.fuelTypes && dto.fuelTypes.length) ? dto.fuelTypes[0] : '0',
      karoserija: (dto.bodyTypes && dto.bodyTypes.length) ? dto.bodyTypes[0] : '0',
      ageTypes: {
        novo: !dto.ageTypes || dto.ageTypes.length === 0 || dto.ageTypes.includes('novo'),
        rabljeno: !dto.ageTypes || dto.ageTypes.length === 0 || dto.ageTypes.includes('rabljeno'),
      },
    }));
  };

  // Load the persisted filter once on mount so saved brands/ranges return after re-login.
  useEffect(() => {
    apiService.getUserFilter().then((res) => applyServerFilter(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the current filter to the user's profile ("Potrdi filter"). Seller type is kept
  // client-side only (its values don't map cleanly to server-side matching).
  const saveFilter = async () => {
    const f = filters;
    const num = (v) => { const n = parseInt(v, 10); return isNaN(n) ? null : n; };
    const ages = [];
    if (f.ageTypes.novo) ages.push('novo');
    if (f.ageTypes.rabljeno) ages.push('rabljeno');
    const dto = {
      brands: f.selectedBrands.length === ALL_BRANDS.length ? [] : f.selectedBrands,
      model: f.model || null,
      minPrice: num(f.priceFrom),
      maxPrice: num(f.priceTo),
      minYear: num(f.letnikFrom),
      maxYear: num(f.letnikTo),
      maxMileage: num(f.mileageTo),
      minMileage: num(f.mileageFrom),
      fuelTypes: (f.gorivo && f.gorivo !== '0') ? [f.gorivo] : [],
      bodyTypes: (f.karoserija && f.karoserija !== '0') ? [f.karoserija] : [],
      ageTypes: ages.length === 2 ? [] : ages,
      sellerTypes: [],
      active: true,
    };
    try {
      await apiService.updateUserFilter(dto);
      setFilterSaved(true);
      setTimeout(() => setFilterSaved(false), 2500);
    } catch (e) {
      alert('Napaka pri shranjevanju filtra. Poskusite znova.');
    }
  };

  // Filtered cars
  const [filteredCars, setFilteredCars] = useState([]);
  
  // Sort state
  const [sortField, setSortField] = useState('scrapedAt');
  const [sortDirection, setSortDirection] = useState('desc');

  const fetchCars = useCallback(async (isAutoRefresh = false) => {
    try {
      if (isAutoRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      const response = await apiService.getRecentCars(48);
      const newCars = response.data;
      
      // Track new cars for animation based on scraped time
      if (isAutoRefresh) {
        const fiveSecondsAgo = new Date(Date.now() - 5000);
        const newlyScrapedCars = newCars.filter(car => {
          const scrapedAt = new Date(car.scrapedAt);
          return scrapedAt > fiveSecondsAgo;
        });
        
        if (newlyScrapedCars.length > 0) {
          console.log('Newly scraped cars:', newlyScrapedCars.length);
          newlyScrapedCars.forEach(car => {
            setAnimatedCarIds(prev => new Set(prev).add(car.id));
          });
          
          // Remove animation after 2 seconds
          setTimeout(() => {
            newlyScrapedCars.forEach(car => {
              setAnimatedCarIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(car.id);
                return newSet;
              });
            });
          }, 2000);
        }
      }
      
      setCars(newCars);
      setTotalPages(1);
      setTotalElements(newCars.length);
    } catch (error) {
      console.error('Error fetching cars:', error);
    } finally {
      if (isAutoRefresh) {
        setIsRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  const fetchStats = async () => {
    try {
      const response = await apiService.getCarStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (brandDropdownOpen && !event.target.closest('.multi-select-container')) {
        setBrandDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [brandDropdownOpen]);

  useEffect(() => {
    fetchCars();
    fetchStats();
  }, [fetchCars]);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCars(true);
      fetchStats();
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchCars]);


  useEffect(() => {
    // Apply filters
    let filtered = cars.filter(car => {
      // Znamka filter (multi-select)
      if (filters.selectedBrands.length < ALL_BRANDS.length && !filters.selectedBrands.includes(car.znamka)) {
        return false;
      }
      
      // Model filter
      if (filters.model && !car.model?.toLowerCase().includes(filters.model.toLowerCase())) {
        return false;
      }
      
      // Price range filter
      const carPrice = parseInt(car.cena?.replace(/\./g, '').replace(/[^0-9]/g, '')) || 0;
      if (filters.priceFrom && carPrice < parseInt(filters.priceFrom)) return false;
      if (filters.priceTo && carPrice > parseInt(filters.priceTo)) return false;
      
      // Year range filter
      const carYear = parseInt(car.letnik) || 0;
      if (filters.letnikFrom && carYear < parseInt(filters.letnikFrom)) return false;
      if (filters.letnikTo && carYear > parseInt(filters.letnikTo)) return false;
      
      // Mileage filter (only max)
      const carMileage = parseInt(car.mileage?.replace(/\./g, '').replace(/[^0-9]/g, '')) || 0;
      if (filters.mileageFrom && carMileage < parseInt(filters.mileageFrom)) return false;
      if (filters.mileageTo && carMileage > parseInt(filters.mileageTo)) return false;
      
      // Gorivo filter
      if (filters.gorivo && filters.gorivo !== '0' && car.gorivo !== filters.gorivo) {
        return false;
      }
      
      // Karoserija filter
      if (filters.karoserija && filters.karoserija !== '0' && car.karoserija !== filters.karoserija) {
        return false;
      }
      if (filters.viewLocation && !car.viewLocation?.toLowerCase().includes(filters.viewLocation.toLowerCase())) {
        return false;
      }
      
      // Age type filter
      const isNovo = car.starost?.toLowerCase() === 'novo';
      const isRabljeno = car.starost?.toLowerCase() === 'rabljeno';
      
      if (!filters.ageTypes.novo && isNovo) return false;
      if (!filters.ageTypes.rabljeno && isRabljeno) return false;
      if (!filters.ageTypes.novo && !filters.ageTypes.rabljeno) return false;
      
      // Seller type filter
      const isTrader = car.sellerStatus?.toLowerCase().includes('trgovec');
      const isIndividual = car.sellerStatus?.toLowerCase().includes('uporabnik');
      
      if (!filters.sellerTypes.trader && isTrader) return false;
      if (!filters.sellerTypes.individual && isIndividual) return false;
      if (!filters.sellerTypes.trader && !filters.sellerTypes.individual) return false;
      
      return true;
    });

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      
      // Handle numeric fields
      if (sortField === 'cena') {
        aVal = parseInt(aVal.replace(/[^0-9]/g, '')) || 0;
        bVal = parseInt(bVal.replace(/[^0-9]/g, '')) || 0;
      }
      if (sortField === 'letnik') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredCars(filtered);
  }, [cars, filters, sortField, sortDirection]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };
  
  const handleBrandToggle = (brand) => {
    setFilters(prev => ({
      ...prev,
      selectedBrands: prev.selectedBrands.includes(brand)
        ? prev.selectedBrands.filter(b => b !== brand)
        : [...prev.selectedBrands, brand]
    }));
  };
  
  const selectAllBrands = () => {
    setFilters(prev => ({ ...prev, selectedBrands: [...ALL_BRANDS] }));
  };
  
  const deselectAllBrands = () => {
    setFilters(prev => ({ ...prev, selectedBrands: [] }));
  };
  
  const filteredBrands = ALL_BRANDS.filter(brand => 
    brand.toLowerCase().includes(brandSearchTerm.toLowerCase())
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('sl-SI', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    // Remove the leading 0 if followed by +
    let cleaned = phone.replace(/^0\+/, '+');
    // Format +386 numbers
    if (cleaned.startsWith('+386')) {
      const number = cleaned.substring(4);
      // Format as +386 41 719 557
      if (number.length >= 8) {
        return `+386 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
      }
    }
    return cleaned;
  };
  
  const formatSellerStatus = (status) => {
    if (!status) return '-';
    return status.toLowerCase().includes('uporabnik') ? 'Fizični' : status;
  };
  
  const formatPrice = (price) => {
    if (!price) return '-';
    // Remove any non-numeric characters except dots
    const numericPrice = price.replace(/[^\d.]/g, '');
    // Parse to number and format with thousands separator
    const number = parseFloat(numericPrice);
    if (isNaN(number)) return price;
    // Format with dot as thousands separator
    return number.toLocaleString('sl-SI') + ' €';
  };

  const openCarLink = (avtonetId) => {
    const url = `https://www.avto.net/Ads/details.asp?id=${avtonetId}`;
    window.open(url, '_blank');
  };

  return (
    <>
      <Navigation />
      <div className="container">
        <div className="cars-table-container">
          <div className="table-header">
            <div className="header-row">
              <h2>
                Avtomobili (zadnjih 48 ur)
              </h2>
              <div className="refresh-indicator">
                {isRefreshing && 'Osvežujem...'}
              </div>
            </div>
            {stats && (
              <div className="stats">
                Zadnjih 48h: {stats.last48h != null ? stats.last48h : (stats.last24h || stats.carsLast24Hours || 0)}
              </div>
            )}
          </div>

          <div className="filters">
            <div className="filter-section">
              <h3>Filtri</h3>
              
              <div className="filter-grid">
                <div className="filter-group">
                  <label>Znamke</label>
                  <div className="multi-select-container">
                    <div 
                      className="multi-select-header"
                      onClick={() => setBrandDropdownOpen(!brandDropdownOpen)}
                    >
                      <span>
                        {filters.selectedBrands.length === ALL_BRANDS.length 
                          ? 'Vse znamke' 
                          : filters.selectedBrands.length === 0
                          ? 'Nobena izbrana'
                          : filters.selectedBrands.length === 1
                          ? filters.selectedBrands[0]
                          : `Izbrane (${filters.selectedBrands.length})`}
                      </span>
                      <span className="dropdown-arrow">{brandDropdownOpen ? '▲' : '▼'}</span>
                    </div>
                    
                    {brandDropdownOpen && (
                      <div className="multi-select-dropdown">
                        <div className="multi-select-search">
                          <input
                            type="text"
                            value={brandSearchTerm}
                            onChange={(e) => setBrandSearchTerm(e.target.value)}
                            placeholder="Išči znamke..."
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        
                        <div className="multi-select-actions">
                          <button onClick={selectAllBrands} className="select-all-btn">
                            Izberi vse
                          </button>
                          <button onClick={deselectAllBrands} className="select-none-btn">
                            Počisti
                          </button>
                        </div>
                        
                        <div className="multi-select-list">
                          {POPULAR_BRANDS.length > 0 && brandSearchTerm === '' && (
                            <>
                              <div className="brand-section-header">Najpopularnejše znamke</div>
                              {POPULAR_BRANDS.map(brand => (
                                <label key={brand} className="multi-select-item">
                                  <input
                                    type="checkbox"
                                    checked={filters.selectedBrands.includes(brand)}
                                    onChange={() => handleBrandToggle(brand)}
                                  />
                                  <span>{brand}</span>
                                </label>
                              ))}
                              <div className="brand-section-header">Vse znamke</div>
                            </>
                          )}
                          
                          {filteredBrands.map(brand => (
                            <label key={brand} className="multi-select-item">
                              <input
                                type="checkbox"
                                checked={filters.selectedBrands.includes(brand)}
                                onChange={() => handleBrandToggle(brand)}
                              />
                              <span>{brand}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="filter-group">
                  <label>Model</label>
                  <input
                    type="text"
                    value={filters.model}
                    onChange={(e) => handleFilterChange('model', e.target.value)}
                    placeholder="Išči po modelu..."
                  />
                </div>
                
                <div className="filter-group-range">
                  <label>Cena (€)</label>
                  <div className="range-inputs">
                    <input
                      type="number"
                      value={filters.priceFrom}
                      onChange={(e) => handleFilterChange('priceFrom', e.target.value)}
                      placeholder="Od"
                    />
                    <span className="range-separator">-</span>
                    <input
                      type="number"
                      value={filters.priceTo}
                      onChange={(e) => handleFilterChange('priceTo', e.target.value)}
                      placeholder="Do"
                    />
                  </div>
                </div>
                
                <div className="filter-group-range">
                  <label>Letnik</label>
                  <div className="range-inputs">
                    <input
                      type="number"
                      value={filters.letnikFrom}
                      onChange={(e) => handleFilterChange('letnikFrom', e.target.value)}
                      placeholder="Od"
                    />
                    <span className="range-separator">-</span>
                    <input
                      type="number"
                      value={filters.letnikTo}
                      onChange={(e) => handleFilterChange('letnikTo', e.target.value)}
                      placeholder="Do"
                    />
                  </div>
                </div>
                
                <div className="filter-group-range">
                  <label>Prevoženi km</label>
                  <div className="range-inputs">
                    <input
                      type="number"
                      value={filters.mileageFrom}
                      onChange={(e) => handleFilterChange('mileageFrom', e.target.value)}
                      placeholder="Od"
                    />
                    <span className="range-separator">-</span>
                    <input
                      type="number"
                      value={filters.mileageTo}
                      onChange={(e) => handleFilterChange('mileageTo', e.target.value)}
                      placeholder="Do"
                    />
                  </div>
                </div>
                
                <div className="filter-group">
                  <label>Gorivo</label>
                  <select
                    value={filters.gorivo}
                    onChange={(e) => handleFilterChange('gorivo', e.target.value)}
                    className="form-control"
                  >
                    <option value="0">Vsi</option>
                    <option value="201">Bencin</option>
                    <option value="202">Diesel</option>
                    <option value="205">Hibridni pogon (vsi)</option>
                    <option value="2054">&nbsp;&nbsp;&nbsp;&nbsp;HEV - hibrid</option>
                    <option value="2056">&nbsp;&nbsp;&nbsp;&nbsp;PHEV - plugin hibrid</option>
                    <option value="207">E-pogon</option>
                    <option value="203">Plin</option>
                  </select>
                </div>
                
                <div className="filter-group">
                  <label>Karoserija</label>
                  <select
                    value={filters.karoserija}
                    onChange={(e) => handleFilterChange('karoserija', e.target.value)}
                    className="form-control"
                  >
                    <option value="0">Vse</option>
                    <option value="19">Microcar</option>
                    <option value="12">Kombilimuzina</option>
                    <option value="11">Limuzina</option>
                    <option value="13">Karavan</option>
                    <option value="14">Enoprostorec</option>
                    <option value="15">SUV</option>
                    <option value="16">Coupe</option>
                    <option value="17">Cabrio</option>
                    <option value="18">Pick-up</option>
                  </select>
                </div>
                
                <div className="filter-group">
                  <label>Lokacija</label>
                  <input
                    type="text"
                    value={filters.viewLocation}
                    onChange={(e) => handleFilterChange('viewLocation', e.target.value)}
                    placeholder="Lokacija..."
                  />
                </div>
                
                <div className="filter-group">
                  <label>Starost vozila</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={filters.ageTypes.novo}
                        onChange={(e) => setFilters(prev => ({
                          ...prev,
                          ageTypes: { ...prev.ageTypes, novo: e.target.checked }
                        }))}
                      />
                      <span>Novo</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={filters.ageTypes.rabljeno}
                        onChange={(e) => setFilters(prev => ({
                          ...prev,
                          ageTypes: { ...prev.ageTypes, rabljeno: e.target.checked }
                        }))}
                      />
                      <span>Rabljeno</span>
                    </label>
                  </div>
                </div>
                
                <div className="filter-group">
                  <label>Tip prodajalca</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label" title="Filtriramo samo fizične osebe" style={{ opacity: 0.45, cursor: 'not-allowed' }}>
                      <input
                        type="checkbox"
                        checked={false}
                        disabled
                        readOnly
                      />
                      <span>Trgovec</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={filters.sellerTypes.individual}
                        onChange={(e) => setFilters(prev => ({
                          ...prev,
                          sellerTypes: { ...prev.sellerTypes, individual: e.target.checked }
                        }))}
                      />
                      <span>Fizični</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="filter-actions">
                <button className="btn btn-primary btn-save-filter" onClick={saveFilter}>Potrdi filter</button>
                {filterSaved && <span className="filter-saved">✓ Filter shranjen</span>}
                <EnableNotifications />
              </div>
            </div>
          </div>

          {loading && cars.length === 0 ? (
            <Spinner label="Nalaganje avtomobilov…" />
          ) : filteredCars.length === 0 ? (
            <div className="no-data">Ni najdenih avtomobilov</div>
          ) : (
            <>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Slika</th>
                      <th onClick={() => handleSort('znamka')}>
                        Znamka {sortField === 'znamka' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('model')}>
                        Model {sortField === 'model' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('cena')}>
                        Cena {sortField === 'cena' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('letnik')}>
                        Letnik {sortField === 'letnik' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('starost')}>
                        Starost {sortField === 'starost' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('mileage')}>
                        Prevoženi km {sortField === 'mileage' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('gorivo')}>
                        Gorivo {sortField === 'gorivo' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('karoserija')}>
                        Karoserija {sortField === 'karoserija' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('viewLocation')}>
                        Lokacija {sortField === 'viewLocation' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('sellerStatus')}>
                        Prodajalec {sortField === 'sellerStatus' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('phone')}>
                        Telefon {sortField === 'phone' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('scrapedAt')}>
                        Datum objave {sortField === 'scrapedAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCars.map((car) => (
                      <tr 
                        key={car.id} 
                        className={animatedCarIds.has(car.id) ? 'new-car-row' : ''}
                        onClick={() => openCarLink(car.avtonetId)}
                      >
                        <td className="cell-image" data-label="Slika">
                          {car.imageUrl ? (
                            <img
                              src={`${IMAGE_ORIGIN}${car.imageUrl}`}
                              alt={`${car.znamka || ''} ${car.model || ''}`.trim()}
                              style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                              loading="lazy"
                            />
                          ) : (
                            '-'
                          )}
                        </td>
                        <td data-label="Znamka">{car.znamka || '-'}</td>
                        <td data-label="Model">{car.model || '-'}</td>
                        <td className="price" data-label="Cena">{formatPrice(car.cena)}</td>
                        <td data-label="Letnik">{car.letnik || '-'}</td>
                        <td data-label="Starost">{car.starost || '-'}</td>
                        <td data-label="Prevoženo">{car.mileage || '-'}</td>
                        <td data-label="Gorivo">{FUEL_TYPES[car.gorivo] || car.gorivo || '-'}</td>
                        <td data-label="Karoserija">{BODY_TYPES[car.karoserija] || car.karoserija || '-'}</td>
                        <td data-label="Lokacija">{car.viewLocation || '-'}</td>
                        <td data-label="Prodajalec">{formatSellerStatus(car.sellerStatus)}</td>
                        <td data-label="Telefon">{formatPhone(car.phone)}</td>
                        <td data-label="Objavljeno">{formatDate(car.scrapedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="pagination">
                <span className="pagination-info">
                  Prikazanih {filteredCars.length} od {cars.length} avtomobilov
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default CarsTable;